import { useCallback, useRef, useState } from 'react';
import { PagingQuery } from 'tea-component';

import {
  fetchRobloxTopChartsMetrics,
  parseRobloxTopChartsResponse,
} from '@intelligence/v3/services/robloxTopCharts';
import { handleTableDefaultSelectGames } from '@intelligence/utils/tableUtils';
import {
  ROBLOX_TOP_CHARTS_DEFAULT_PAGE_SIZE,
  ROBLOX_TOP_CHARTS_DEFAULT_SELECT_GAME_NUM,
} from '../constants';
import { ROBLOX_TOP_CHARTS_DEFAULT_SORT_KEY } from '../config/robloxTopChartsSort';
import { buildRobloxTopChartsFetchParams } from '../utils/buildRobloxTopChartsFetchParams';

import type { SortByItemType } from '@intelligence/components/TableComponents/components/SortBy';

import type { TableSelectionType, TableSelectStatusType } from '@intelligence/components/TableComponents/components/TableCheckbox';
import type { IMainGenre } from '@intelligence/types/common';
import type { IRobloxTopChartsFilterValues, IRobloxTopChartsRow } from '../types';

interface IFetchRobloxTopChartsTableOptions {
  resetPage?: boolean;
  resetSelection?: boolean;
  gameIds?: string[];
  rankGameIds?: string[];
  sortKey?: string;
  sortOrder?: 'asc' | 'desc';
  /** 排序切换时清空表格并展示 loading */
  clearRecords?: boolean;
  fetchChart?: boolean;
  onAfterFetch?: (payload: {
    chartGameIds: string[];
    sortKey: string;
    sortOrder: 'asc' | 'desc';
  }) => void | Promise<void>;
}

const getSelectionGameIds = (list: TableSelectionType[]): string[] => {
  return list
    .map((item) => String(item?.gameId || (item as IRobloxTopChartsRow)?.game_id || ''))
    .filter(Boolean);
};

export default function useRobloxTopChartsTable(
  robloxGenreOptionsRef?: React.MutableRefObject<IMainGenre[]>,
) {
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState<IRobloxTopChartsRow[]>([]);
  const [total, setTotal] = useState(0);
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(ROBLOX_TOP_CHARTS_DEFAULT_PAGE_SIZE);
  const [tableSelection, setTableSelection] = useState<TableSelectionType[]>([]);
  const [tableSelectionAll, setTableSelectionAll] = useState<TableSelectionType[]>([]);
  const [selectedGameId, setSelectedGameId] = useState('');
  const [sortKey, setSortKey] = useState(ROBLOX_TOP_CHARTS_DEFAULT_SORT_KEY);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const gameIdsRef = useRef<string[]>([]);
  const rankGameIdsRef = useRef<string[]>([]);
  const sortKeyRef = useRef(ROBLOX_TOP_CHARTS_DEFAULT_SORT_KEY);
  const sortOrderRef = useRef<'asc' | 'desc'>('desc');
  const pageIndexRef = useRef(1);
  const pageSizeRef = useRef(ROBLOX_TOP_CHARTS_DEFAULT_PAGE_SIZE);
  const tableSelectionRef = useRef<TableSelectionType[]>([]);

  const applyDefaultSelection = useCallback((list: IRobloxTopChartsRow[]) => {
    const defaultGames = handleTableDefaultSelectGames(
      list.slice(0, ROBLOX_TOP_CHARTS_DEFAULT_SELECT_GAME_NUM),
      'game_id',
    );
    setTableSelectionAll(defaultGames);
    setTableSelection(defaultGames);
    tableSelectionRef.current = defaultGames;
    return defaultGames;
  }, []);

  const fetchTable = useCallback(async (
    filterValues: IRobloxTopChartsFilterValues,
    options?: IFetchRobloxTopChartsTableOptions,
  ): Promise<{
    chartGameIds: string[];
    sortKey: string;
    sortOrder: 'asc' | 'desc';
  }> => {
    const nextGameIds = options?.gameIds !== undefined
      ? options.gameIds.filter(Boolean)
      : gameIdsRef.current;
    const nextRankGameIds = options?.rankGameIds !== undefined
      ? options.rankGameIds.filter(Boolean)
      : rankGameIdsRef.current;

    if (options?.rankGameIds !== undefined) {
      rankGameIdsRef.current = nextRankGameIds;
      gameIdsRef.current = [];
    } else if (options?.gameIds !== undefined) {
      gameIdsRef.current = nextGameIds;
      rankGameIdsRef.current = [];
    }

    const requestGameIds = rankGameIdsRef.current.length
      ? undefined
      : gameIdsRef.current;
    const requestRankGameIds = rankGameIdsRef.current.length
      ? rankGameIdsRef.current
      : undefined;
    const nextPageIndex = options?.resetPage ? 1 : pageIndexRef.current;
    const nextPageSize = pageSizeRef.current;
    const shouldResetSelection = options?.resetSelection ?? options?.resetPage ?? false;
    const nextSortKey = options?.sortKey ?? sortKeyRef.current;
    const nextSortOrder = options?.sortOrder ?? sortOrderRef.current;

    setSelectedGameId(
      rankGameIdsRef.current[0]
      || gameIdsRef.current[0]
      || '',
    );
    sortKeyRef.current = nextSortKey;
    sortOrderRef.current = nextSortOrder;
    setSortKey(nextSortKey);
    setSortOrder(nextSortOrder);
    setLoading(true);

    if (shouldResetSelection || options?.fetchChart || options?.clearRecords) {
      setRecords([]);
      setTotal(0);
    }

    try {
      const params = buildRobloxTopChartsFetchParams(filterValues, {
        page: nextPageIndex,
        pageSize: nextPageSize,
        gameIds: requestGameIds,
        rankGameIds: requestRankGameIds,
        sortKey: nextSortKey,
        sortOrder: nextSortOrder,
        robloxGenreOptions: robloxGenreOptionsRef?.current,
      });
      const response = await fetchRobloxTopChartsMetrics(params);
      const { list, total: nextTotal } = parseRobloxTopChartsResponse(response);

      let chartGameIds: string[] = [];

      if (nextPageIndex === 1 && shouldResetSelection) {
        const defaultGames = applyDefaultSelection(list);
        chartGameIds = getSelectionGameIds(defaultGames);
      } else {
        chartGameIds = getSelectionGameIds(tableSelectionRef.current);
      }

      if (options?.fetchChart && options?.onAfterFetch) {
        await Promise.resolve(options.onAfterFetch({
          chartGameIds,
          sortKey: nextSortKey,
          sortOrder: nextSortOrder,
        }));
      }

      setRecords(list);
      setTotal(nextTotal);
      setPageIndex(nextPageIndex);
      pageIndexRef.current = nextPageIndex;

      return {
        chartGameIds,
        sortKey: nextSortKey,
        sortOrder: nextSortOrder,
      };
    } catch (error) {
      console.debug('[RobloxTopCharts] fetch table failed', error);
      setRecords([]);
      setTotal(0);
      if (shouldResetSelection) {
        setTableSelection([]);
        setTableSelectionAll([]);
        tableSelectionRef.current = [];
      }

      if (options?.fetchChart && options?.onAfterFetch) {
        await Promise.resolve(options.onAfterFetch({
          chartGameIds: [],
          sortKey: nextSortKey,
          sortOrder: nextSortOrder,
        }));
      }

      return {
        chartGameIds: [],
        sortKey: nextSortKey,
        sortOrder: nextSortOrder,
      };
    } finally {
      setLoading(false);
    }
  }, [applyDefaultSelection]);

  const handlePagingChange = useCallback((
    filterValues: IRobloxTopChartsFilterValues,
    paging: PagingQuery,
  ) => {
    const nextPageIndex = paging.pageIndex || 1;
    const nextPageSize = paging.pageSize || ROBLOX_TOP_CHARTS_DEFAULT_PAGE_SIZE;

    pageIndexRef.current = nextPageIndex;
    pageSizeRef.current = nextPageSize;
    setPageSize(nextPageSize);

    fetchTable(filterValues, {
      resetPage: false,
      resetSelection: false,
    });
  }, [fetchTable]);

  const handleCheckGame = useCallback((
    list: TableSelectionType[],
    _status: TableSelectStatusType,
  ) => {
    setTableSelection(list);
    tableSelectionRef.current = list;
  }, []);

  const handleSelectGame = useCallback((
    filterValues: IRobloxTopChartsFilterValues,
    gameId: string,
  ) => {
    if (!gameId) {
      return;
    }

    fetchTable(filterValues, {
      gameIds: [gameId],
      resetPage: true,
      resetSelection: true,
    });
  }, [fetchTable]);

  const handleClearSearch = useCallback((filterValues: IRobloxTopChartsFilterValues) => {
    fetchTable(filterValues, {
      gameIds: [],
      resetPage: true,
      resetSelection: true,
    });
  }, [fetchTable]);

  const handleSortChange = useCallback((
    filterValues: IRobloxTopChartsFilterValues,
    sortItem: SortByItemType,
  ) => {
    const nextSortKey = sortItem?.key || ROBLOX_TOP_CHARTS_DEFAULT_SORT_KEY;
    const nextSortOrder = sortItem?.sortVal === 'asc' ? 'asc' : 'desc';

    return fetchTable(filterValues, {
      sortKey: nextSortKey,
      sortOrder: nextSortOrder,
      gameIds: [],
      resetPage: true,
      resetSelection: true,
      clearRecords: true,
    });
  }, [fetchTable]);

  return {
    loading,
    records,
    total,
    pageIndex,
    pageSize,
    selectedGameId,
    tableSelection,
    tableSelectionAll,
    sortKey,
    sortOrder,
    fetchTable,
    handlePagingChange,
    handleCheckGame,
    handleSelectGame,
    handleClearSearch,
    handleSortChange,
  };
}
